import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { 
  Search, 
  Play, 
  Image as ImageIcon, 
  X, 
  Check, 
  Library,
  Gamepad2,
  Calendar,
  Loader2
} from 'lucide-react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import { format } from 'date-fns';

import { API_BASE_URL } from '../../config';

interface PostMedia {
  id: string;
  filePath: string;
  thumbnailPath?: string;
  mediaType: string;
  createdAt: string;
  post?: {
    id: string;
    game: {
      name: string;
    };
  };
}

interface MediaGallerySelectorProps {
  open: boolean;
  onClose: () => void;
  onSelect: (media: PostMedia) => void;
}

export default function MediaGallerySelector({
  open,
  onClose,
  onSelect
}: MediaGallerySelectorProps): JSX.Element {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const token = localStorage.getItem('token');

  const { data: allMedia = [], isLoading } = useQuery<PostMedia[]>({
    queryKey: ['userMedia'],
    queryFn: async () => {
      const response = await axios.get(`${API_BASE_URL}/social/user-media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: open && !!token
  });

  const filteredMedia = allMedia.filter(item => {
    const gameName = item.post?.game?.name || 'Standalone';
    return gameName.toLowerCase().includes(searchQuery.toLowerCase()) ||
           item.filePath.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const handleConfirm = () => {
    const selected = allMedia.find(m => m.id === selectedId);
    if (selected) {
      onSelect(selected);
      onClose();
    }
  };

  const getMediaUrl = (filePath: string) => {
    return `${API_BASE_URL}/social/media?path=${encodeURIComponent(filePath)}&token=${token}`;
  };

  const getThumbnailUrl = (filePath: string, w: number = 300) => {
    return `${API_BASE_URL}/social/media/thumbnail?path=${encodeURIComponent(filePath)}&w=${w}&token=${token}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[90vw] h-[80vh] bg-background border-border p-0 flex flex-col overflow-hidden text-foreground">
        <DialogHeader className="p-6 border-b border-border flex flex-row items-center justify-between shrink-0">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Library className="h-6 w-6 text-primary" />
            Select from Gallery
          </DialogTitle>
        </DialogHeader>

        <div className="p-4 border-b border-border bg-muted/50">
          <div className="relative group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
            <Input 
              placeholder="Search by game or filename..." 
              className="pl-11 bg-card border-border rounded-xl h-11 text-foreground focus:ring-2 focus:ring-primary/20 transition-all"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <ScrollArea className="flex-1 p-6">
          {isLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredMedia.length === 0 ? (
            <div className="h-64 flex flex-col items-center justify-center text-muted-foreground gap-4">
              <Library className="h-12 w-12 opacity-20" />
              <p>No media found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map((item) => (
                <div 
                  key={item.id}
                  className={cn(
                    "relative aspect-square rounded-2xl overflow-hidden cursor-pointer border-2 transition-all group",
                    selectedId === item.id ? "border-primary ring-4 ring-primary/20" : "border-border hover:border-border"
                  )}
                  onClick={() => setSelectedId(item.id)}
                >
                  {item.mediaType === 'video' ? (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      {item.thumbnailPath ? (
                        <img src={getThumbnailUrl(item.thumbnailPath)} className="w-full h-full object-cover" alt="" />
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <Play className="h-8 w-8 text-primary opacity-50" />
                          <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-widest">Video</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img src={getThumbnailUrl(item.filePath)} className="w-full h-full object-cover" alt="" />
                  )}

                  {/* Overlay Info */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent p-3 flex flex-col justify-end">
                    <div className="flex items-center gap-1 text-[10px] font-black text-white uppercase tracking-widest truncate">
                      <Gamepad2 className="h-3 w-3" />
                      {item.post?.game?.name || 'Standalone'}
                    </div>
                  </div>

                  {selectedId === item.id && (
                    <div className="absolute top-2 right-2 bg-primary rounded-full p-1 shadow-lg">
                      <Check className="h-4 w-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        <DialogFooter className="p-6 border-t border-border bg-muted/50 gap-3">
          <Button variant="ghost" onClick={onClose} className="rounded-xl font-bold">
            Cancel
          </Button>
          <Button 
            disabled={!selectedId} 
            onClick={handleConfirm}
            className="bg-primary hover:bg-primary/90 text-white font-bold rounded-xl px-8"
          >
            Select Media
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

